import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Linkedin, Sparkles, Lock, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  Loader2, RefreshCw, FileText, ClipboardCheck, Wand2, ArrowRight, Info, Copy, Check,
  Mail, Briefcase,
} from 'lucide-react';
import { useClaudeAI } from '../hooks/useClaudeAI';
import DailyLimitModal from '../components/DailyLimitModal';

const scoreColor = (score) => (score >= 80 ? '#22C55E' : score >= 60 ? '#F59E0B' : '#EF4444');
const scoreBg    = (score) => (score >= 80 ? '#F0FDF4' : score >= 60 ? '#FFFBEB' : '#FEF2F2');
const scoreLabel = (score) => (score >= 80 ? 'Strong profile' : score >= 60 ? 'Good foundation' : 'Needs work');

const ScoreGauge = ({ score, size = 136 }) => {
  const r = (size - 16) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (Math.max(0, Math.min(100, score)) / 100) * circ;
  const color = scoreColor(score);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={12} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={12}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
    </svg>
  );
};

const HOW_TO_STEPS = [
  'Open your LinkedIn profile (on desktop)',
  'Click the More button under your name → Save to PDF',
  'Open the PDF, select all (Ctrl/Cmd+A), copy, and paste it below',
];

const CopyBlock = ({ label, text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 14, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#EF1D28', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
        <button type="button" onClick={handleCopy}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #E5E7EB', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, fontWeight: 700, color: copied ? '#059669' : '#6B7280', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
          {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div style={{ fontSize: 13.5, color: '#1E293B', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
};

export const LinkedInAuditPage = ({ user, onOpenJobSearch }) => {
  const navigate = useNavigate();
  const {
    analyzeLinkedInProfile, generateLinkedInRewrites,
    loading, error, dailyLimitError, clearDailyLimitError,
  } = useClaudeAI();

  const [profileText, setProfileText] = useState('');
  const [result, setResult]           = useState(null);
  const [rewrites, setRewrites]       = useState(null);
  const [howToOpen, setHowToOpen]     = useState(false);
  const [action, setAction]           = useState(null);

  const isPro     = user?.plan === 'pro';
  const scoring   = loading && action === 'score';
  const rewriting = loading && action === 'rewrite';

  const handleScore = async () => {
    if (!profileText.trim() || scoring) return;
    setRewrites(null);
    setAction('score');
    try {
      const r = await analyzeLinkedInProfile(profileText.trim());
      setResult(r);
    } catch (_) { /* error surfaced via hook state */ }
  };

  const handleRewrites = async () => {
    if (!isPro || !result || rewriting) return;
    setAction('rewrite');
    try {
      const r = await generateLinkedInRewrites(profileText.trim(), result.biggestGaps);
      setRewrites(r);
    } catch (_) { /* error surfaced via hook state */ }
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ marginBottom: 26 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#E8F0FE', color: '#0A66C2', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 800, marginBottom: 14 }}>
          <Linkedin size={13} /> LinkedIn Audit
        </div>
        <h1 style={{ margin: '0 0 8px', fontSize: 27, fontWeight: 900, color: '#0F172A', letterSpacing: -0.6 }}>LinkedIn Profile Score Checker</h1>
        <p style={{ margin: 0, fontSize: 14.5, color: '#64748B', lineHeight: 1.7, maxWidth: 620 }}>
          Check your LinkedIn profile and get an instant 0-100 score with a section-by-section breakdown — free, no upload. Pro unlocks line-by-line rewrites of your headline, About, and experience.
        </p>
      </div>

      {/* ── Input card ── */}
      <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 20, padding: 24, boxShadow: '0 10px 32px rgba(15,23,42,0.05)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 14, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: '#E8F0FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Info size={15} color="#0A66C2" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>
              <strong style={{ color: '#0F172A' }}>Paste your profile, don't link it.</strong> LinkedIn hides most profile data from automated tools, so link-based scans are inaccurate. A Save-to-PDF export has your full headline, About, experience and skills, so it scores precisely.
            </div>
            <button type="button" onClick={() => setHowToOpen(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#EF1D28', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: '8px 0 0', fontFamily: 'Inter, sans-serif' }}>
              How do I get my profile text? {howToOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {howToOpen && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                {HOW_TO_STEPS.map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#E2E8F0', color: '#475569', fontSize: 10.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 12.5, color: '#475569' }}>{step}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
          Paste your profile <span style={{ background: '#F0FDF4', color: '#15803D', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 800, marginLeft: 6, verticalAlign: 1 }}>MOST ACCURATE</span>
        </label>
        <textarea
          value={profileText}
          onChange={e => setProfileText(e.target.value.slice(0, 12000))}
          rows={9}
          placeholder="Paste your headline, About, experience, education, and skills here…"
          style={{ width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif', lineHeight: 1.6, marginBottom: 6 }}
          onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
          onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', marginBottom: 16 }}>{profileText.length}/12000</div>

        {error && action === 'score' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            <AlertTriangle size={14} />{error}
          </div>
        )}

        <button type="button" onClick={handleScore} disabled={!profileText.trim() || scoring}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: !profileText.trim() ? '#F3F4F6' : 'linear-gradient(135deg,#EF1D28 0%,#FF5C5C 100%)', color: !profileText.trim() ? '#9CA3AF' : 'white', border: 'none', borderRadius: 12, padding: '14px 14px', fontSize: 14.5, fontWeight: 700, cursor: !profileText.trim() ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: !profileText.trim() ? 'none' : '0 4px 16px rgba(239,29,40,0.30)' }}>
          {scoring
            ? <><Loader2 size={17} style={{ animation: 'spin 0.8s linear infinite' }} />Analyzing your profile…</>
            : <>{result ? <RefreshCw size={16} /> : <Sparkles size={16} />} {result ? 'Re-score my profile' : 'Score my profile'}</>}
        </button>
      </div>

      {result && (
        <>
          {/* ── Score gauge ── */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 20, padding: 26, boxShadow: '0 10px 32px rgba(15,23,42,0.05)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: 136, height: 136, flexShrink: 0 }}>
              <ScoreGauge score={result.overall} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 30, fontWeight: 900, color: '#0F172A', lineHeight: 1 }}>{result.overall}</div>
                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>/ 100</div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: scoreBg(result.overall), color: scoreColor(result.overall), borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 800, marginBottom: 10 }}>
                {result.summary || scoreLabel(result.overall)}
              </div>
              <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>
                Your LinkedIn profile score is {result.overall}/100 for now. You're {result.overall >= 80 ? 'in great shape' : result.overall >= 60 ? 'on the right track' : 'a few sections away'} from a profile that gets recruiters to click first.
              </div>
            </div>
          </div>

          {/* ── Section breakdown ── */}
          <div style={{ background: 'white', border: '1px solid #F0F2F5', borderRadius: 20, padding: 24, boxShadow: '0 10px 32px rgba(15,23,42,0.05)', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 16 }}>Section-by-section breakdown</div>
            <div style={{ display: 'grid', gap: 18 }}>
              {result.sections.map(sec => (
                <div key={sec.id}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1E293B' }}>{sec.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: scoreColor(sec.score) }}>{sec.score}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 99, background: '#F1F5F9', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${sec.score}%`, borderRadius: 99, background: scoreColor(sec.score), transition: 'width 1s ease' }} />
                  </div>
                  {sec.issues.length > 0 && (
                    <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                      {sec.issues.slice(0, 3).map((issue, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, color: '#94A3B8' }}>
                          <span style={{ color: '#CBD5E1', flexShrink: 0 }}>•</span>{issue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── Biggest gaps ── */}
          {result.biggestGaps.length > 0 && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 20, padding: 22, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <AlertTriangle size={15} color="#DC2626" />
                <span style={{ fontSize: 13.5, fontWeight: 800, color: '#B91C1C' }}>Biggest gaps</span>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {result.biggestGaps.map((gap, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, color: '#7F1D1D' }}>
                    <span style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }}>✕</span>{gap}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Premium rewrites ── */}
          <div style={{ background: isPro ? 'white' : 'linear-gradient(180deg,#0F172A 0%,#172554 100%)', border: isPro ? '1px solid #F0F2F5' : 'none', borderRadius: 20, padding: 28, marginBottom: 20, boxShadow: isPro ? '0 10px 32px rgba(15,23,42,0.05)' : 'none' }}>
            {!isPro ? (
              <div style={{ textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Lock size={22} color="#FCA5A5" />
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'white', marginBottom: 8 }}>Unlock line-by-line rewrites</div>
                <div style={{ fontSize: 13.5, color: '#CBD5E1', lineHeight: 1.7, marginBottom: 20 }}>
                  Pro rewrites your headline, About section, and top experience bullet based on the exact gaps found above, so you can copy, paste, and publish.
                </div>
                <button type="button" onClick={() => navigate('/pricing')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'white', color: '#0F172A', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  Upgrade to Pro <ArrowRight size={15} />
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: rewrites ? 18 : 0, flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', marginBottom: 4 }}>Line-by-line rewrites</div>
                    <div style={{ fontSize: 13, color: '#64748B' }}>Pro-only: rewritten headline, About, and top bullet.</div>
                  </div>
                  <button type="button" onClick={handleRewrites} disabled={rewriting}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 13.5, fontWeight: 700, cursor: rewriting ? 'default' : 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap' }}>
                    {rewriting
                      ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} />Rewriting…</>
                      : <><Wand2 size={15} />{rewrites ? 'Regenerate' : 'Generate rewrites'}</>}
                  </button>
                </div>
                {error && action === 'rewrite' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 16 }}>
                    <AlertTriangle size={14} />{error}
                  </div>
                )}
                {rewrites && (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <CopyBlock label="Headline" text={rewrites.headline} />
                    <CopyBlock label="About" text={rewrites.about} />
                    <CopyBlock label="Top experience bullet" text={rewrites.experienceBullet} />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Cross-sell footer ── */}
      <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 20, padding: '22px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A', marginBottom: 4 }}>Your LinkedIn profile mirrors your resume</div>
        <div style={{ fontSize: 12.5, color: '#64748B', marginBottom: 16 }}>Keep both in sync with the rest of your MarsResume toolkit.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12 }}>
          {[
            { icon: FileText,    label: 'Pro CV',          desc: 'Build a matching resume', onClick: () => navigate('/builder/resume/new') },
            { icon: Briefcase,   label: 'Tailor to a Job',  desc: 'Match it to a role fast', onClick: () => (onOpenJobSearch ? onOpenJobSearch() : navigate('/dashboard?view=job_search')) },
            { icon: ClipboardCheck, label: 'Cover Letter',  desc: 'Pair with a cover letter', onClick: () => navigate('/builder/cover_letter/new') },
          ].map(t => (
            <button key={t.label} type="button" onClick={t.onClick}
              style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'white', border: '1px solid #E5E7EB', borderRadius: 14, padding: '14px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <t.icon size={15} color="#EF1D28" />
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }}>{t.label}</div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
    </div>
  );
};
